import { Bullet } from "./Bullet";
import { Application, PointData } from "pixi.js";
import { Player } from "./Player";
import { Keyboard } from "./Keyboard";
import { Enemy } from "./Enemy";
import { DebugOverlay } from "./DebugOverlay";
import { Subject } from "../utils/Subject";
import { FilthTracker } from "./FilthTracker";
import { Camera } from "./Camera";
import { Item } from "./Item";
import { BeeType } from "./BeeType";
import { Ground } from "./Ground";
import { Hive } from "./Hive";
import { FloatingText } from "./FloatingText";
import { DamageTick } from "./DamageTick";
import { Mouse } from "./Mouse";

export function getTargetCoordsNormalized(event: any): { x: number, y: number } {
	const rect = event.currentTarget.getBoundingClientRect() as DOMRect
	return {
		x: (event.clientX - rect.left) / rect.width - 0.5,
		y: (event.clientY - rect.top) / rect.height - 0.5
	}
}

export interface GameMouseEvent {
	type: 'down' | 'move'
	point: PointData,
}

export class Game {
	public filthTracker = new FilthTracker();
	stage: number = 0;
	over: 'won' | 'lost' | null = null
	items: Item[] = [];
	spawnEnemies = this.stage > 0;
	camera: Camera = new Camera(this.app.stage, this.app.canvas);
	debugOverlay: DebugOverlay = new DebugOverlay(this.app.stage, this.app.ticker);
	player: Player = new Player(this.camera.container, this.filthTracker);
	enemies: Enemy[] = [];
	hives: Hive[] = [];
	ground: Ground = new Ground(this.camera.container, this, 10);
	bullets: Bullet[] = [];
	texts: (FloatingText | DamageTick)[] = [];
	shouldRestart: boolean = false;
	showHitboxes: boolean = false;

	mouse = new Mouse();
	private debugControlsEnabled: boolean = false;

	public get keys() {
		return this.keyboard.keys;
	}

	public constructor(
		private readonly isFirstRun: boolean,
		private readonly app: Application,
		private readonly uiUpdateRequested: Subject<never>,
		private readonly keyboard: Keyboard,
	) {
		// if (isFirstRun) {
		// 	for (let i = 0; i < 16; i++) {
		// 		this.player.addBee(BeeType.DiesFirstBee);
		// 	}
		// 	for (let i = 0; i < 4; i++) {
		// 		this.player.addBee(BeeType.MeleeBee);
		// 	}
		// 	this.nextStage();
		// 	this.player.godmode = true;
		// }
	}

	public tick(deltaMS: number): void {
		if (this.player.beeCount <= 0) {
			if (!this.over) {
				this.over = 'lost'
				this.uiUpdateRequested.next();
			}
			return
		}

		this.keyboard.tick(deltaMS);
		this.mouse.tick();
		this.debugOverlay.tick(deltaMS);
		this.ground.tick(this, deltaMS);
		this.player.tick(this, deltaMS);
		this.camera.tick(deltaMS, this.player);
		for (const enemy of this.enemies) enemy.tick(deltaMS, this);
		for (const bullet of this.bullets) bullet.tick(deltaMS, this);
		for (const hive of this.hives) hive.tick(deltaMS, this);
		for (const item of this.items) item.tick(deltaMS, this);
		for (const text of this.texts) text.tick(deltaMS, this);

		for (let i = this.enemies.length - 1; i >= 0; i--) {
			if (this.enemies[i].destroyed) this.enemies.splice(i, 1);
		}
		for (let i = this.bullets.length - 1; i >= 0; i--) {
			if (this.bullets[i].destroyed) this.bullets.splice(i, 1);
		}
		for (let i = this.texts.length - 1; i >= 0; i--) {
			if (this.texts[i].container.destroyed) this.texts.splice(i, 1);
		}
		for (let i = this.items.length - 1; i >= 0; i--) {
			if (this.items[i].destroyed) this.items.splice(i, 1);
		}

		if (this.player.beeCount <= 0) this.player.container.destroy({ children: true });
		this.handleDebugControls();

		if (this.filthTracker.isDirty) {
			this.uiUpdateRequested.next();
			this.filthTracker.clean();
		}
	}

	private handleDebugControls() {
		if (this.keys.shiftM.pressed) {
			this.debugControlsEnabled = !this.debugControlsEnabled;
			this.texts.push(new FloatingText(this.app.stage, `debugControlsEnabled=${this.debugControlsEnabled}`, 1000));
		}
		if (!this.debugControlsEnabled) return;
		if (this.keys._1.pressed) {
			this.spawnEnemies = !this.spawnEnemies;
			this.texts.push(new FloatingText(this.app.stage, `spawnEnemies=${this.spawnEnemies}`, 1000));
		}
		if (this.keys._2.pressed) {
			this.player.godmode = !this.player.godmode;
			this.texts.push(new FloatingText(this.app.stage, `godmode=${this.player.godmode}`, 1000));
		}
		if (this.keys._3.pressed) {
			this.texts.push(new FloatingText(this.app.stage, `nextStage`, 1000));
			this.nextStage();
		}
		if (this.keys._4.pressed) {
			this.texts.push(new FloatingText(this.app.stage, `restart`, 1000));
			this.restart();
		}
		if (this.keys._5.pressed) {
			this.texts.push(new DamageTick(this.camera.container, this.player, 10));
		}
		if (this.keys._6.pressed) {
			this.texts.push(new FloatingText(this.app.stage, `ultrakill`, 1000));
			for (const enemy of this.enemies) enemy.dealDamage(this, 10000);
		}
		if (this.keys._7.pressed) {
			this.showHitboxes = !this.showHitboxes;
			this.texts.push(new FloatingText(this.app.stage, `showHitboxes=${this.showHitboxes}`, 1000));
		}
		if (this.keys._8.pressed) {
			this.app.ticker.speed *= 0.9;
			this.texts.push(new FloatingText(this.app.stage, `speed=${this.app.ticker.speed}`, 1000));
		}
		if (this.keys._9.pressed) {
			this.app.ticker.speed *= 1 / (0.9);
			this.texts.push(new FloatingText(this.app.stage, `speed=${this.app.ticker.speed}`, 1000));
		}
		if (this.keys.shift1.pressed) {
			this.hives.push(new Hive(this.camera.container, this.player));
		}
		if (this.keys.shift2.pressed) {

		}
		if (this.keys.shift3.pressed) {
			this.player.takeDamage(this, 1);
			this.texts.push(new FloatingText(this.app.stage, `damaged`, 1000));
		}
		if (this.keys.shift4.pressed) {
			this.player.noclip = !this.player.noclip;
			this.texts.push(new FloatingText(this.app.stage, `noclip=${this.player.noclip}`, 1000));
		}
		if (this.keys.shift6.pressed) {
			this.player.addBee(BeeType.DiesFirstBee);
			this.texts.push(new FloatingText(this.app.stage, `added bee`, 1000));
		}
		if (this.keys.shift7.pressed) {
			this.player.addBee(BeeType.MeleeBee);
			this.texts.push(new FloatingText(this.app.stage, `added bee`, 1000));
		}
		if (this.keys.shift8.pressed) {
			this.player.addBee(BeeType.BulletBee);
			this.texts.push(new FloatingText(this.app.stage, `added bee`, 1000));
		}
		if (this.keys.shift9.pressed) {
			this.player.addBee(BeeType.DashBee);
			this.texts.push(new FloatingText(this.app.stage, `added bee`, 1000));
		}
	}

	public nextStage() {
		this.goToStage(this.stage + 1);
	}

	public restart() {
		this.goToStage(this.stage);
		this.player.reset();
	}

	public goToStage(stage: number) {
		if (stage > 3) {
			this.over = 'won'
			this.filthTracker.soil()
			return;
		}

		for (const enemy of this.enemies) enemy.destroy();
		this.enemies = [];
		for (const bullet of this.bullets) bullet.destroy();
		this.bullets = [];
		for (const item of this.items) item.destroy();
		this.items = [];
		for (const hive of this.hives) hive.destroy();
		this.hives = []
		this.stage = stage;
		this.spawnEnemies = stage > 0;
		this.player.x = 0;
		this.player.y = 0;
		this.camera.x = 0;
		this.camera.y = 0;
		this.ground.destroy();
		this.ground = new Ground(this.camera.container, this, 10);
		this.filthTracker.soil();
	}
}
